import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { fetchOrdersListRequest } from '../../modules/shop/actions';
import { IoIosArrowBack, IoIosSearch } from "react-icons/io";
import { FiCopy } from "react-icons/fi";
import { message, Skeleton, Modal, Input } from 'antd';
import httpClient from '../../utils/HttpClient';
import { ORDERS_URL } from '../../constants/Url';
import CustomPagination from '../../components/common/CustomPagination';
import OrderShipperModal from '../../components/OrderShipperModal';
import PaymentModal from '../../components/PaymentModal';
import { useTranslation } from 'react-i18next';

export default function MyOrdersPage({ isHide }) {
  const { data: rawOrders = [], total = 0, loading } = useSelector(state => state.shop.orderList);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState('全部');
  const [searchQuery, setSearchQuery] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [selectedPaymentOrder, setSelectedPaymentOrder] = useState(null);
  
  const getApiStatus = (tab) => {
    switch (tab) {
      case '交易取消': return 'pendingPayment';
      case '待发货': return 'pendingShipment';
      case '已发货': return 'shippedOrder';
      case '已完成': return 'completed';
      default: return 'ALL';
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setAppliedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    setPageNo(1);
  }, [activeTab, appliedSearch]);

  useEffect(() => {
    dispatch(fetchOrdersListRequest({ status: getApiStatus(activeTab), keyword: appliedSearch, pageNo, pageSize }));
  }, [dispatch, activeTab, appliedSearch, pageNo, pageSize]);
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [cancelOrderId, setCancelOrderId] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  const handleCancelOrder = (orderId) => {
    setCancelOrderId(orderId);
    setCancelReason('');
    setCancelModalVisible(true);
  };

  const confirmCancelOrder = async () => {
    if (!cancelReason.trim()) {
      return message.warning(t('text_enter_cancel_reason'));
    }
    setCancelling(true);
    try {
      const res = await httpClient.put(`${ORDERS_URL}/${cancelOrderId}`, { reason: cancelReason });
      if (res.data && (res.data.status === 200 || (res.data.status === undefined && (res.status === 200 || res.status === 201)))) {
        message.success(t('text_order_cancelled'));
        setCancelModalVisible(false);
        dispatch(fetchOrdersListRequest());
      } else {
        message.error(t(res.data?.message || 'text_order_cancel_failed'));
      }
    } catch (error) {
      console.error('Error cancelling order:', error);
      message.error(t(error?.message || error?.data?.message || 'text_order_cancel_failed'));
    } finally {
      setCancelling(false);
    }
  };

  const TABS = [
    {
      label: t("text_all"),
      value: "全部"
    },
    {
      label: t("text_status_wait_ship"),
      value: "待发货"
    },
    {
      label: t("text_Shipped"),
      value: "已发货"
    },
    {
      label: t("text_status_wait_payment"),
      value: "交易取消"
    },
  ];

  // Map backend status to UI status
  const mapStatus = (order) => {
    if (!order) return t('text_unknown_status');
    
    const s = (order.status || '').toLowerCase();
    
    // pendingPayment -> 交易取消
    if (s === 'pending' || s === 'unpaid' || s === 'waiting') return t('text_status_wait_payment');
    
    // pendingShipment -> 待发货 (status is "confirmed", "paid", "processing")
    if (s === 'confirmed' || s === 'paid' || s === 'processing') return t('text_status_wait_ship');
    
    // shippedOrder -> 已发货 (status is "completed", "shipped", "delivered")
    if (s === 'shipped' || s === 'completed' || s === 'delivered') return t('text_Shipped');
    
    // cancelled -> 已取消
    if (s === 'cancelled' || s === 'canceled') return t('text_cancelled');
    
    return t('text_status_wait_payment');
  };

  // Combine real redux orders with mock data if empty to show off the UI
  const orders = rawOrders.length > 0 ? rawOrders.map(o => ({
    ...o,
    id: o.orderId || o._id,
    uiStatus: mapStatus(o),
    totalAmount: o.total || o.totalAmount,
    totalQty: o.totalQty || (o.items ? o.items.reduce((sum, i) => sum + (i.qty || i.quantity || 1), 0) : 0),
    items: o.items || []
  })) : [];

  const filteredOrders = orders.filter(o => {
    return true; // Default to include all orders if no filters are applied
    const matchTab = activeTab === '全部' || o.uiStatus === activeTab;
    const matchSearch = String(o.id).includes(searchQuery) || o.items.some(i => (i.title || i.name || i.productName || '').includes(searchQuery));
    return matchTab && matchSearch;
  });

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    message.success(t('text_copied_ordernum'));
  };

  return (
    <div className="w-full min-h-screen flex flex-col font-sans">
      {
        !isHide && (
          <>
            {/* Desktop Header & Breadcrumb */}
            <div className="hidden lg:flex esi-std-container w-full mx-auto pt-10 pb-6 items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="text-gray-500 hover:text-[#e80d44] transition-colors flex items-center gap-2 font-medium"
              >
                <IoIosArrowBack className="text-xl" />
                <span>{t("text_back")}</span>
              </button>
              <span className="text-gray-300">|</span>
              <h1 className="text-[22px] font-bold text-gray-900 tracking-wide">{t("text_my_order")}</h1>
            </div>

            {/* Mobile Header */}
            <div className="lg:hidden pt-4 pb-3 px-4">
              <div className="flex items-center justify-center relative">
                <button
                  onClick={() => navigate(-1)}
                  className="absolute left-0 text-[24px] text-gray-800"
                >
                  <IoIosArrowBack />
                </button>
                <h1 className="text-[17px] font-semibold text-gray-900 tracking-wide">{t("text_my_order")}</h1>
              </div>
            </div>
          </>
        )
      }

      <div className="esi-std-container w-full mx-auto flex flex-col pb-12">
        {/* Controls: Tabs & Search */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 lg:gap-6 mb-6 lg:static lg:bg-transparent pt-2 pb-3 lg:p-0">
          
          {/* Tabs */}
          <div className="flex overflow-x-auto hide-scrollbar gap-6 lg:gap-8 px-2 lg:px-0">
            {TABS.map(tab => (
              <div 
                key={tab.value}
                onClick={() => setActiveTab(tab.value)}
                className={`relative pb-2 whitespace-nowrap cursor-pointer select-none transition-colors ${
                  activeTab === tab.value 
                    ? 'text-gray-900 font-bold' 
                    : 'text-gray-400 font-medium hover:text-gray-600'
                }`}
              >
                <span className="text-[15px] tracking-wide">{tab?.label}</span>
                {activeTab === tab.value && (
                  <div className="absolute bottom-0 left-0 w-full h-[3px] bg-gradient-to-r from-[#8e78ff] to-[#51b1ff] rounded-full shadow-[0_2px_4px_rgba(142,120,255,0.3)]"></div>
                )}
              </div>
            ))}
          </div>

          {/* Search Bar */}
          <div className="flex items-center gap-3 w-full lg:w-[320px]">
            <div className="flex-1 flex items-center bg-white border border-gray-300 rounded-xl px-4 py-2.5 transition-all">
              <IoIosSearch 
                className="text-gray-400 text-[20px] shrink-0 cursor-pointer hover:text-[#e80d44] transition-colors" 
                onClick={() => setAppliedSearch(searchQuery)}
              />
              <input 
                type="text" 
                placeholder={t("text_search_order")} 
                className="bg-transparent border-none outline-none ml-2 text-[14px] w-full text-gray-700 placeholder-gray-400"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setAppliedSearch(searchQuery);
                  }
                }}
              />
            </div>
          </div>
        </div>

        {/* Order List */}
        <div className="flex-1 space-y-4 lg:space-y-5">
          {loading ? (
            Array(3).fill(null).map((_, index) => (
              <div key={index} className="bg-white rounded-2xl p-5 lg:p-6 shadow-sm border border-gray-100 flex flex-col">
                <div className="flex justify-between items-center mb-5 pb-4 border-b border-gray-50">
                  <Skeleton.Input active size="small" style={{ width: 150 }} />
                  <Skeleton.Input active size="small" style={{ width: 60 }} />
                </div>
                <div className="flex flex-col lg:flex-row gap-5 lg:items-start pt-2">
                  <div className="flex-1 flex gap-4">
                    <Skeleton.Image active className="!w-[84px] !h-[84px] lg:!w-[100px] lg:!h-[100px] rounded-lg" />
                    <div className="flex flex-col pt-1 flex-1">
                      <Skeleton active paragraph={{ rows: 1 }} title={{ width: '80%' }} />
                    </div>
                  </div>
                  <div className="flex lg:flex-col items-center lg:items-end justify-between lg:justify-start pt-4 lg:pt-0 lg:pl-6 lg:w-[200px] shrink-0 gap-4 lg:gap-5 border-t lg:border-t-0 lg:border-l border-gray-50 h-full">
                    <div className="flex items-baseline gap-1 lg:flex-col lg:items-end lg:gap-0.5 w-full">
                       <Skeleton.Input active size="small" style={{ width: 80 }} />
                    </div>
                    <div className="flex gap-2.5">
                      <Skeleton.Button active shape="round" style={{ width: 80 }} />
                      <Skeleton.Button active shape="round" style={{ width: 80 }} />
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : filteredOrders.length === 0 ? (
            <div className="text-center py-20 text-gray-400 text-sm bg-white rounded-2xl shadow-sm border border-gray-100">
              {t("text_no_order")}
            </div>
          ) : (
            filteredOrders.map(order => {
              const firstItem = order.items && order.items.length > 0 ? order.items[0] : null;
              const totalQty = order.totalQty;

              return (
                <div key={order.id} className="bg-white rounded-2xl p-5 lg:p-6 shadow-sm border border-gray-100 hover:border-gray-200 transition-colors flex flex-col">
                  
                  {/* Card Header */}
                  <div className="flex justify-between items-center mb-4 pb-3 border-b border-gray-50">
                    <div className="flex items-center gap-2">
                      <span className="text-[13px] lg:text-[14px] text-gray-900 font-bold">{t("text_order_num")}: {order.id}</span>
                      <button onClick={() => copyToClipboard(order.id)} className="text-gray-400 hover:text-gray-600 transition-colors p-1">
                        <FiCopy className="text-sm" />
                      </button>
                    </div>
                    <span className={`text-[13px] font-bold ${order.uiStatus === '已取消' ? 'text-gray-400' : 'text-[#e2005a]'}`}>
                      {order.uiStatus}
                    </span>
                  </div>

                  {/* Card Body - Item List */}
                  <div className="flex flex-col gap-4 pt-2">
                    {order.items && order.items.map((item, idx) => (
                      <div key={item.id || item._id || idx} className="flex gap-3">
                        <div className="w-[84px] h-[84px] rounded-lg overflow-hidden flex items-center justify-center ">
                          <img 
                            src={item.images?.[0] || ""} 
                            alt={item?.productName || ""} 
                            className="w-full h-full object-cover mix-blend-multiply rounded-md" 
                          />
                        </div>
                        <div className="flex flex-col flex-1 pt-0.5">
                          {/* Title & Price Row */}
                          <div className="flex justify-between items-start gap-3">
                            <h3 className="text-[14px] lg:text-[15px] text-gray-900 font-medium leading-snug line-clamp-2">
                              {item?.productName || ''}
                            </h3>
                            <div className="flex flex-col items-end shrink-0 leading-tight mt-0.5">
                              <span className="text-[14px] lg:text-[15px] font-bold text-gray-900">
                                ￥{item.unitPrice ? item.unitPrice.toFixed(2) : '0.00'}
                              </span>
                              <span className="text-[12px] lg:text-[13px] text-gray-400 mt-1.5">
                                x{item.qty || item.quantity || 1}
                              </span>
                            </div>
                          </div>
                          
                          {/* Category / Description */}
                          {(item.categoryName || item.description) && (
                             <p className="text-gray-400 text-[12px] lg:text-[13px] line-clamp-1 mt-1 pr-10">
                               {item.categoryName || item.description}
                             </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Card Footer - Total & Actions */}
                  <div className="mt-4 pt-3 flex flex-col gap-3">
                    {/* Total */}
                    <div className="flex justify-end items-center gap-1">
                      <span className="text-[13px] text-gray-800">{t("text_actual_paid")}</span>
                      <span className="text-[16px] lg:text-[18px] font-bold text-gray-900">
                        ￥{order.totalAmount ? order.totalAmount.toFixed(2) : '0.00'}
                      </span>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-between items-center mt-1">
                      <div 
                        className="text-gray-500 text-[13px] cursor-pointer hover:text-gray-800 font-medium border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors"
                        onClick={() => navigate(`/my-orders/${order.id}`)}
                      >
                        {t("text_view_details")}
                      </div>
                      <div className="flex gap-2.5">
                        {(order.uiStatus === t("text_status_wait_payment") && (activeTab === '全部' || activeTab === '交易取消')) && (
                          <>
                            {/* <button 
                              onClick={() => handleCancelOrder(order.id)}
                              className="whitespace-nowrap px-4 py-2 rounded-lg text-[13px] text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 transition-colors font-medium"
                            >
                              {t("button_cancel_order")}
                            </button> */}
                            {
                              order?.paymentStatus === "waiting" ? "" :
                            <button 
                              onClick={() => {
                                setSelectedPaymentOrder(order);
                                setPaymentModalVisible(true);
                              }}
                              className="whitespace-nowrap px-4 py-2 rounded-lg text-[13px] font-bold text-white bg-[#cc2944] hover:bg-[#b02038] transition-colors"
                            >
                              {t("button_paynow")}
                            </button>
                            }
                          </>
                        )}
                         {/* activeTab === '待收货' */}
                        {/* {
                          activeTab === '待收货' && (
                            <button onClick={() => setIsOrderModalOpen(true)} className="whitespace-nowrap px-4 py-2 rounded-lg text-[13px] font-bold text-black bg-[#edf0f1] transition-colors">
                              {t("text_check_tracking")}
                            </button>
                          )
                        } */}
                      </div>
                    </div>
                  </div>
                  <OrderShipperModal isOrderModalOpen={isOrderModalOpen} setIsOrderModalOpen={setIsOrderModalOpen} order={order} />
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        {total > 0 && (
          <div className="mt-8 flex justify-end pb-4">
            <CustomPagination
              total={total}
              current={pageNo}
              pageSize={pageSize}
              onChange={(page, limit) => {
                setPageNo(page);
                if (limit) setPageSize(limit);
              }}
            />
          </div>
        )}
      </div>

      <Modal
        title={t("button_cancel_order")}
        open={cancelModalVisible}
        onOk={confirmCancelOrder}
        onCancel={() => setCancelModalVisible(false)}
        confirmLoading={cancelling}
        okText={t("text_confirm")}
        cancelText={t("button_cancel")}
        centered
      >
        <div className="py-4">
          <p className="mb-2 text-gray-600 font-medium">{t("text_enter_cancel_reason")}:</p>
          <Input.TextArea 
            rows={3} 
            placeholder="" 
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
          />
        </div>
      </Modal>
      <PaymentModal 
        open={paymentModalVisible} 
        onCancel={() => setPaymentModalVisible(false)} 
        order={selectedPaymentOrder} 
      />
    </div>
  );
}
